/**
 * sp_select_contract_element_report returns the element's current value alongside the
 * version that preceded it (prior_value_amt and its period / effective date). These
 * helpers turn that pair into the comparison the Comp Plan Details cards present.
 */

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const isBlank = (value) => value === null || value === undefined || value === "";

/**
 * MySQL DATETIME columns arrive either as "2025-10-01 06:00:00" or as a full ISO
 * timestamp. Reading the month off the leading date part avoids the month-shift that
 * `new Date(...)` introduces either side of UTC.
 */
export const formatEffectiveDate = (value) => {
  if (isBlank(value)) return "";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return String(value);
  const month = MONTHS[Number(match[2]) - 1];
  return month ? `${month} ${match[1]}` : match[0];
};

/** The period name is the label the client knows the version by; the date is the fallback. */
export const versionLabel = (periodName, effectiveDate) => {
  if (!isBlank(periodName)) return String(periodName);
  return formatEffectiveDate(effectiveDate);
};

/**
 * Returns null when there is nothing to compare — either the element is on its first
 * version, or one of the two values is not numeric — which leaves the card in its
 * plain, undecorated state.
 */
export const getVersionDelta = (element) => {
  if (!element || isBlank(element.prior_value_amt)) return null;

  const current = Number(element.value_amt);
  const prior = Number(element.prior_value_amt);
  if (isNaN(current) || isNaN(prior)) return null;

  const change = current - prior;
  const direction = change > 0 ? "up" : change < 0 ? "down" : "flat";

  return {
    current,
    prior,
    change,
    // A prior value of zero has no meaningful percentage change, so the card falls
    // back to showing the absolute movement instead.
    percent: prior === 0 ? null : (change / Math.abs(prior)) * 100,
    direction,
  };
};

const ARROWS = { up: "\u25B2", down: "\u25BC", flat: "\u2014" };

/** Badge text for a delta: an arrow plus the percentage, or the absolute change when there is no baseline. */
export const formatDelta = (delta, formatAmount) => {
  if (!delta) return "";
  if (delta.direction === "flat") return `${ARROWS.flat} No change`;

  const arrow = ARROWS[delta.direction];
  if (delta.percent === null) {
    const sign = delta.change > 0 ? "+" : "\u2212";
    return `${arrow} ${sign} ${formatAmount(Math.abs(delta.change))}`;
  }

  return `${arrow} ${Math.abs(delta.percent).toFixed(1)} %`;
};

/** Signed percentage for the comparison popover's Delta row. */
export const formatDeltaPercent = (delta) => {
  if (!delta || delta.percent === null) return "";
  const sign = delta.percent > 0 ? "+" : delta.percent < 0 ? "\u2212" : "";
  return `${sign} ${Math.abs(delta.percent).toFixed(1)} %`.trim();
};
